import React, { useState, useEffect } from 'react';
import api from '../api';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { ArrowLeft, Hash, LockKeyhole, Mail } from 'lucide-react';
import './Login.css';
import loginImg from '../assets/login.png';
import { clearUIRulesCache } from '../components/UIElementGuard';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loginMethod, setLoginMethod] = useState('password');
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);

  // Forgot Password States
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [resetOtpSent, setResetOtpSent] = useState(false);
  const [resetOtpVerified, setResetOtpVerified] = useState(false);
  const [resetOtp, setResetOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const navigate = useNavigate();
  const location = useLocation();

  // Finishes the LMS -> Nexus SSO hand-off: exchanges the authenticated session for
  // an authorization code and sends the browser to the Nexus callback. Runs both when
  // the student already has a session and right after they log in manually.
  const approveSso = async (search) => {
    const searchParams = new URLSearchParams(search);
    try {
      const params = new URLSearchParams();
      params.append('client_id', searchParams.get('client_id') || '');
      params.append('redirect_uri', searchParams.get('redirect_uri') || '');
      params.append('response_type', searchParams.get('response_type') || '');
      if (searchParams.get('state')) params.append('state', searchParams.get('state'));
      if (searchParams.get('nonce')) params.append('nonce', searchParams.get('nonce'));

      const response = await api.post('/oauth/authorize/approve', params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });

      if (response.data && response.data.redirect_to) {
        window.location.href = response.data.redirect_to;
      }
    } catch (err) {
      console.error("SSO Approval failed:", err);
      setError("Single Sign-On failed. Please log in manually.");
      setLoading(false);
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('error') === 'deactivated') {
      setError("Your university account has been deactivated. Please contact the system administrator.");
    }

    // SSO Interception Logic
    const isSso = searchParams.get('sso') === 'true';
    const existingToken = localStorage.getItem('token');

    // Without a session on this origin the login form is shown instead and
    // handleLoginSuccess resumes the hand-off — otherwise the student would just
    // land on their dashboard and Nexus would never receive the authorization code.
    if (isSso && existingToken) {
      setLoading(true);
      approveSso(location.search);
    }
  }, [location.search]);

  const handleLoginSuccess = (data) => {
    clearUIRulesCache();
    
    // Clear old session to prevent role or permission leaks
    const keysToPreserve = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('read_remarks') || key.startsWith('seen_student_jobs'))) {
        keysToPreserve.push({ key, value: localStorage.getItem(key) });
      }
    }
    localStorage.clear();
    keysToPreserve.forEach(item => localStorage.setItem(item.key, item.value));

    localStorage.setItem('token', data.access_token);
    localStorage.setItem('role', data.role);
    if (data.base_role) {
      localStorage.setItem('base_role', data.base_role);
    }
    localStorage.setItem('user_id', data.user_id || (data.user && data.user.id));
    if (data.permissions) {
      localStorage.setItem('permissions', JSON.stringify(data.permissions));
    }
    if (data.accessible_modules) {
      localStorage.setItem('accessible_modules', JSON.stringify(data.accessible_modules));
    }
    if (data.user) {
      localStorage.setItem('user', JSON.stringify(data.user));
    }
    window.dispatchEvent(new Event('auth_changed'));

    // If the student is already authenticated but the approve call failed (expired
    // session, transient error), the manual login below must still finish the hand-off.
    if (new URLSearchParams(window.location.search).get('sso') === 'true') {
      setLoading(true);
      approveSso(window.location.search);
      return;
    }

    const role = (data.role || '').toLowerCase();
    const baseRole = (data.base_role || '').toLowerCase();

    if (role === 'university' || baseRole === 'university') navigate('/uni-dashboard');
    else if (role === 'pop' || baseRole === 'pop') navigate('/pop-dashboard');
    else if (role === 'admin' || baseRole === 'admin' || role === 'system_admin' || baseRole === 'system_admin') navigate('/admin-dashboard');
    else if (role === 'support' || baseRole === 'support') navigate('/admin-dashboard');
    else if (role === 'student' || baseRole === 'student') navigate('/student-dashboard');
    else if (role === 'faculty' || baseRole === 'faculty') navigate('/faculty-dashboard');
    else if (role === 'tpo' || baseRole === 'tpo') navigate('/tpo-dashboard');
    else if (role === 'compliance' || baseRole === 'compliance') navigate('/compliance-dashboard');
    else if (data.permissions || baseRole === 'custom_role') {
      navigate('/uni-dashboard');
    } else {
      setError('Unknown User Role');
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const params = new URLSearchParams();
      params.append('username', email.trim());
      params.append('password', password); // We don't usually trim passwords, but let's check email first

      const response = await api.post('/login', params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });
      handleLoginSuccess(response.data);
    } catch (err) {
      setError(
        err.response?.data?.detail || 
        (err.response ? 'Invalid email or password.' : 'Unable to connect to backend server. Please check your connection.')
      );
    }
  };

  const handleRequestOTP = async () => {
    if (!email) {
      setError('Please enter your email first');
      return;
    }
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      await api.post('/request-otp', { email, purpose: 'login' });
      setOtpSent(true);
      setSuccess('OTP sent to your email!');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPLogin = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const response = await api.post('/verify-otp', { email, otp, purpose: 'login' });
      handleLoginSuccess(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid OTP');
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const response = await api.get('/auth/google?context=login');
      window.location.href = response.data.auth_url;
    } catch (err) {
      setError('Failed to initiate Google login');
    }
  };

  // Forgot Password Handlers
  const handleForgotPasswordClick = (e) => {
    e.preventDefault();
    setForgotPasswordMode(true);
    setError('');
    setSuccess('');
    setResetOtpSent(false);
    setResetOtpVerified(false);
    setResetOtp('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleRequestResetOTP = async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      await api.post('/forgot-password/request-otp', { email, purpose: 'password_reset' });
      setResetOtpSent(true);
      setSuccess('Password reset OTP sent to your email!');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyResetOTP = async () => {
    if (!resetOtp || resetOtp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await api.post('/forgot-password/verify-otp', { email, otp: resetOtp, purpose: 'password_reset' });
      setResetOtpVerified(true);
      setSuccess('OTP verified! Please enter your new password.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setError('');
    setLoading(true);
    try {
      await api.post('/forgot-password/reset', {
        email,
        otp: resetOtp,
        new_password: newPassword
      });
      setSuccess('Password reset successful! Redirecting to login...');
      setTimeout(() => {
        setForgotPasswordMode(false);
        setResetOtpSent(false);
        setResetOtpVerified(false);
        setResetOtp('');
        setNewPassword('');
        setConfirmPassword('');
        setEmail('');
        setSuccess('');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setForgotPasswordMode(false);
    setResetOtpSent(false);
    setResetOtpVerified(false);
    setResetOtp('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess('');
  };

  const searchParams = new URLSearchParams(location.search);
  const isSso = searchParams.get('sso') === 'true';
  const needsLoginForSso = isSso && !loading && !localStorage.getItem('token');

  if (isSso && loading) {
    return (
      <div className="login-wrapper" style={{ justifyContent: 'center', alignItems: 'center', background: '#f8fafc' }}>
        <div style={{ textAlign: 'center', color: '#1e293b' }}>
          <div style={{ margin: '0 auto 20px', width: '40px', height: '40px', border: '4px solid #e2e8f0', borderTop: '4px solid #2563eb', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
          <h2>Authorizing...</h2>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  return (
    <div className="login-wrapper">
      <div className="login-split">
        {/* Left Side: Image */}
        <div className="login-image-side">
          <img src={loginImg} alt="EdVergenceX Login" />
          <div className="image-overlay">
            <h2>Welcome to EdVergenceX</h2>
            <p>Bridging the gap between world-class industry leadership and academic excellence.</p>
          </div>
        </div>

        {/* Right Side: Form */}
        <div className="login-form-side">
          <div className="login-card">
            <div className="login-header">
              <h2>Welcome Back</h2>
              <p>Sign in to your account to continue</p>
            </div>

            {/* Google Login Button */}
            {!forgotPasswordMode && (
              <>
                <div className="social-container">
                  <button className="social-btn" onClick={handleGoogleLogin}>
                    <img src="https://cdn-icons-png.flaticon.com/512/2991/2991148.png" alt="Google" />
                    Sign in with Google
                  </button>
                </div>

                <div className="divider">
                  <span>OR CONTINUE WITH EMAIL</span>
                </div>

                {/* Login Method Toggle */}
                <div className="login-toggle">
                  <button
                    className={`toggle-btn ${loginMethod === 'password' ? 'active' : ''}`}
                    onClick={() => { setLoginMethod('password'); setError(''); setSuccess(''); setOtpSent(false); }}
                  >
                    Password
                  </button>
                  <button
                    className={`toggle-btn ${loginMethod === 'otp' ? 'active' : ''}`}
                    onClick={() => { setLoginMethod('otp'); setError(''); setSuccess(''); }}
                  >
                    OTP on Email
                  </button>
                </div>
              </>
            )}

            {needsLoginForSso && (
              <div style={{
                padding: '10px 14px',
                borderRadius: '8px',
                background: 'rgba(37, 99, 235, 0.1)',
                border: '1px solid rgba(37, 99, 235, 0.25)',
                color: 'var(--primary)',
                fontSize: '0.875rem',
                fontWeight: 600,
                textAlign: 'center'
              }}>
                Sign in to continue to your course.
              </div>
            )}

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            {/* Password Login Form */}
            {!forgotPasswordMode && loginMethod === 'password' && (
              <form onSubmit={handlePasswordLogin} className="login-form">
                <div className="input-group">

                  <div className="input-wrapper">
                    <span className="input-icon"><Mail size={18} aria-hidden="true" /></span>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="you@example.com"
                      autoComplete="username"
                    />
                  </div>
                </div>

                <div className="input-group">

                  <div className="input-wrapper">
                    <span className="input-icon"><LockKeyhole size={18} aria-hidden="true" /></span>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      placeholder="••••••••"
                      autoComplete="current-password"
                    />
                  </div>
                </div>

                <div className="form-options">
                  <a href="#" className="forgot-password" onClick={handleForgotPasswordClick}>Forgot password?</a>
                </div>

                <button type="submit" className="login-submit-btn">Sign In</button>
              </form>
            )}

            {/* Forgot Password Form */}
            {forgotPasswordMode && (
              <div className="login-form">
                <button
                  className="back-button"
                  onClick={handleBackToLogin}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#0891b2',
                    cursor: 'pointer',
                    fontSize: '14px',
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}
                >
                  <ArrowLeft size={16} aria-hidden="true" /> Back to Login
                </button>

                <h3 style={{ textAlign: 'center', marginBottom: '1rem', color: 'var(--proto-white)' }}>
                  Reset Password
                </h3>

                {!resetOtpSent && (
                  <>
                    <div className="input-group">
                      <label>Email Address</label>
                      <div className="input-wrapper">
                        <span className="input-icon"><Mail size={18} aria-hidden="true" /></span>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          placeholder="you@example.com"
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      className="login-submit-btn"
                      onClick={handleRequestResetOTP}
                      disabled={loading}
                    >
                      {loading ? 'Sending...' : 'Send Reset Code'}
                    </button>
                  </>
                )}

                {resetOtpSent && !resetOtpVerified && (
                  <>
                    {window.location.hostname === 'localhost' && (
                      <div className="dev-otp-hint" style={{
                        // marginBottom: '1rem',
                        // padding: '10px',
                        // background: '#fff3cd',
                        // border: '1px solid #ffeeba',
                        // borderRadius: '8px',
                        // fontSize: '0.85rem',
                        // color: '#856404'
                      }}>
                        {/* <strong>Developer Hint:</strong> Check your <b>Backend Terminal</b> for the reset code. */}
                      </div>
                    )}
                    <div className="input-group">
                      <label>Enter Reset Code</label>
                      <div className="input-wrapper">
                        <span className="input-icon"><Hash size={18} aria-hidden="true" /></span>
                        <input
                          type="text"
                          value={resetOtp}
                          onChange={(e) => setResetOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          required
                          placeholder="123456"
                          maxLength={6}
                          className="otp-input"
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      className="login-submit-btn"
                      onClick={handleVerifyResetOTP}
                      disabled={loading}
                    >
                      {loading ? 'Verifying...' : 'Verify Code'}
                    </button>
                    <button
                      type="button"
                      className="resend-btn"
                      onClick={handleRequestResetOTP}
                      disabled={loading}
                    >
                      {loading ? 'Sending...' : 'Resend Code'}
                    </button>
                  </>
                )}

                {resetOtpVerified && (
                  <form onSubmit={handleResetPassword}>
                    <div className="input-group">
                      <label>New Password</label>
                      <div className="input-wrapper">
                        <span className="input-icon"><LockKeyhole size={18} aria-hidden="true" /></span>
                        <input
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          required
                          placeholder="Enter new password"
                          minLength={6}
                          autoComplete="new-password"
                        />
                      </div>
                    </div>
                    <div className="input-group">
                      <label>Confirm New Password</label>
                      <div className="input-wrapper">
                        <span className="input-icon"><LockKeyhole size={18} aria-hidden="true" /></span>
                        <input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                          placeholder="Re-enter new password"
                          minLength={6}
                          autoComplete="new-password"
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      className="login-submit-btn"
                      disabled={loading}
                    >
                      {loading ? 'Resetting...' : 'Reset Password'}
                    </button>
                  </form>
                )}
              </div>
            )}

            {/* OTP Login Form */}
            {!forgotPasswordMode && loginMethod === 'otp' && (
              <form onSubmit={handleOTPLogin} className="login-form">
                {otpSent && (
                  <div style={{ textAlign: 'center', marginBottom: '1rem' }}>

                    {window.location.hostname === 'localhost' && (
                      <div className="dev-otp-hint" style={{
                        // marginTop: '1rem',
                        // padding: '10px',
                        // background: '#fff3cd',
                        // border: '1px solid #ffeeba',
                        // borderRadius: '8px',
                        // fontSize: '0.85rem',
                        // color: '#856404'
                      }}>
                        {/* <strong>Developer Hint:</strong> Check your <b>Backend Terminal</b> for the verification code. */}
                      </div>
                    )}
                  </div>
                )}
                <div className="input-group">
                  <label>Email Address</label>
                  <div className="input-wrapper">
                    <span className="input-icon"><Mail size={18} aria-hidden="true" /></span>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="you@example.com"
                      disabled={otpSent}
                    />
                  </div>
                </div>

                {!otpSent ? (
                  <button
                    type="button"
                    className="login-submit-btn"
                    onClick={handleRequestOTP}
                    disabled={loading}
                  >
                    {loading ? 'Sending...' : 'Send OTP'}
                  </button>
                ) : (
                  <>
                    <div className="input-group">
                      <label>Enter OTP</label>
                      <div className="input-wrapper">
                        <span className="input-icon"><Hash size={18} aria-hidden="true" /></span>
                        <input
                          type="text"
                          value={otp}
                          onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          required
                          placeholder="123456"
                          maxLength={6}
                          className="otp-input"
                        />
                      </div>
                    </div>
                    <button type="submit" className="login-submit-btn">Verify & Sign In</button>
                    <button
                      type="button"
                      className="resend-btn"
                      onClick={handleRequestOTP}
                      disabled={loading}
                    >
                      {loading ? 'Sending...' : 'Resend OTP'}
                    </button>
                  </>
                )}
              </form>
            )}

            <p className="signup-prompt">
              Don't have an account? <Link to="/register">Sign up</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
